<template>
  <v-container flat fluid>
    <v-card>
      <v-card-title class="primary white--text">
        <span class="title">Charge Rates</span>
        <v-spacer></v-spacer>
        <v-btn
          fab
          x-small
          color="blue"
          @click="newChargeRate"
          :disabled="!canAddRate"
          :dark="canAddRate"
        >
          <font-awesome-icon size="lg" icon="plus" />
        </v-btn>
      </v-card-title>

      <v-form>
        <v-container flat fluid class="rates">
          <v-row
            v-for="(chargeRate, index) in client.rates"
            :key="'c' + index"
            no-gutters
          >
            <v-col cols="12">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <v-select
                    v-model="chargeRate.template"
                    label=" Candidate Type"
                    :items="candidateTypes"
                    outlined
                    filled
                    hide-details
                    item-text="name"
                    return-object
                  >
                    <template v-slot:prepend>
                      <span class="font-weight-bold grey--text">{{
                        index + 1
                      }}</span>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-combobox
                    v-model="chargeRate.roles"
                    :items="
                      chargeRate.template ? chargeRate.template.roles : []
                    "
                    label="Roles"
                    placeholder="Select values"
                    outlined
                    filled
                    hide-details
                    multiple
                    small-chips
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        close
                        small
                        @click:close="removeRole(chargeRate, index)"
                      >
                        <span>{{ item }}</span>
                      </v-chip>
                    </template>
                  </v-combobox>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <v-select
                    v-model="chargeRate.sites"
                    :items="sites"
                    label="Sites"
                    placeholder="All sites"
                    outlined
                    filled
                    hide-details
                    item-text="name"
                    multiple
                    small-chips
                    clearable
                  >
                    <template v-slot:selection="{ item, index }">
                      <v-chip
                        close
                        small
                        @click:close="removeSite(chargeRate, index)"
                      >
                        <span>{{ item.name }}</span>
                      </v-chip>
                    </template>
                    <template v-slot:append-outer>
                      <v-tooltip top color="green">
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            small
                            icon
                            color="green"
                            @click="copyRate(chargeRate, index)"
                          >
                            <font-awesome-icon size="lg" icon="copy" />
                          </v-btn>
                        </template>
                        <span>Copy Rate</span>
                      </v-tooltip>
                      <v-tooltip top color="grey">
                        <template v-slot:activator="{ on }">
                          <v-btn
                            v-on="on"
                            small
                            icon
                            color="grey"
                            @click="removeRate(index)"
                          >
                            <font-awesome-icon size="lg" icon="minus-circle" />
                          </v-btn>
                        </template>
                        <span>Remove Rate</span>
                      </v-tooltip>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="auto">
                  <div class="text-caption">Break</div>
                  <vue-timepicker
                    v-model="chargeRate.breaks"
                    auto-scroll
                    format="HH:mm"
                    input-width="80px"
                  ></vue-timepicker>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-subheader>
                Rates
                <!-- <v-switch v-model="chargeRate.payParity" class="ml-5" label="Pay-Parity"></v-switch> -->
                <v-spacer></v-spacer>
                <v-btn
                  fab
                  x-small
                  color="blue"
                  @click="newRate(chargeRate)"
                  dark
                >
                  <font-awesome-icon size="lg" icon="plus" />
                </v-btn>
              </v-subheader>
              <v-divider class="mb-4"></v-divider>
              <v-row
                v-for="(rate, index) in chargeRate.rates"
                :key="'r' + index"
              >
                <v-col cols="12" sm="6" md="4" lg="3" xl="2" class="py-0">
                  <v-combobox
                    v-model="rate.type"
                    label="Type"
                    :items="types"
                    outlined
                    filled
                    hide-details
                    clearable
                  ></v-combobox>
                </v-col>
                <v-col cols="auto" class="py-0">
                  <v-radio-group v-model="rate.period" row dense hide-details>
                    <v-radio label="Days" :value="'Days'"></v-radio>
                    <v-radio label="Nights" :value="'Nights'"></v-radio>
                  </v-radio-group>
                  <v-radio-group
                    v-model="rate.frequency"
                    row
                    dense
                    hide-details
                    @change="changeFrequency(rate)"
                  >
                    <v-radio label="Hourly" :value="'Hourly'"></v-radio>
                    <v-radio label="Set Rate" :value="'Set Rate'"></v-radio>
                  </v-radio-group>
                </v-col>

                <v-col
                  v-if="rate.frequency == 'Hourly'"
                  class="py-0"
                  style="min-width: 100px; max-width: 100px"
                >
                  <v-text-field
                    v-model="rate.hours"
                    label="Hours"
                    placeholder="FLAT"
                    outlined
                    filled
                    type="number"
                    hide-details
                    autocomplete="off"
                  ></v-text-field>
                </v-col>
                <v-col class="py-0" style="min-width: 100px; max-width: 100px">
                  <v-currency-field
                    v-model="rate.rate"
                    label="Rate"
                    currency="GBP"
                    filled
                    outlined
                  />
                </v-col>
                <v-col
                  v-if="rate.hours"
                  class="py-0"
                  style="min-width: 100px; max-width: 100px"
                >
                  <v-currency-field
                    v-model="rate.overtime"
                    label="Overtime"
                    placeholder="0.00"
                    currency="GBP"
                    filled
                    outlined
                  />
                </v-col>
                <v-col class="py-0" v-if="chargeRate.payParity">
                  <v-currency-field
                    v-model="rate.postRate"
                    label="Post Rate"
                    currency="GBP"
                    filled
                    outlined
                  />
                </v-col>
                <v-col
                  v-if="rate.hours && chargeRate.payParity"
                  class="py-0"
                  style="min-width: 125px; max-width: 125px"
                >
                  <v-currency-field
                    v-model="rate.postOvertime"
                    label="Post Overtime"
                    currency="GBP"
                    filled
                    outlined
                  />
                </v-col>
              </v-row>
              <v-divider></v-divider>
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card>
  </v-container>
</template>

<script>
import { mapState, mapActions } from "vuex";

export default {
  props: {
    client: Object,
  },
  data() {
    return {
      types: [
        "Weekday",
        "Saturday",
        "Sunday",
        "Weekend",
        "Bank Holiday",
        "All Hours",
      ],
    };
  },
  computed: {
    ...mapState({
      templates: (state) => state.templates,
    }),
    candidateTypes() {
      return this.templates.items.filter((template) => {
        return !template.predefined && template.type == "Candidate";
      });
    },
    candidateSkills() {
      return [];
    },
    canAddRate() {
      return true;
    },
    sites() {
      return this.client.sites.filter((s) => {
        return s.name;
      });
    },
  },
  methods: {
    checkForData() {
      if (!this.client.rates) {
        this.$set(this.client, "rates", []);
        this.newChargeRate();
      }
    },
    newChargeRate() {
      this.client.rates.push({
        template: null,
        roles: [],
        sites: [],
        rates: [
          {
            period: "Days",
            frequency: "Hourly",
          },
        ],
        breaks: "00:00",
      });
    },
    newRate(chargeRate) {
      chargeRate.rates.push({});
    },
    removeRole(rate, index) {
      rate.roles.splice(index, 1);
    },
    removeSite(rate, index) {
      rate.sites.splice(index, 1);
    },
    removeRate(index) {
      this.client.rates.splice(index, 1);
    },
    copyRate(rate, index) {
      var newRate = JSON.parse(JSON.stringify(rate));
      delete newRate._id;
      this.client.rates.splice(index, 0, newRate);
    },
    changeFrequency(rate) {
      if (rate.frequency != "Hourly") {
        rate.overtime = null;
        rate.hours = null;
      }
    },
  },
  mounted() {
    this.checkForData();
  },
};
</script>

<style>
.rates .v-input--selection-controls {
  margin-bottom: 0px !important;
  margin-top: 0px !important;
}
</style>
